import { AxiosProgressEvent } from 'axios';
import { ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ValidationEntrySeverity } from '../../../../../react-commons/components/ValidationEntrySeverity';
import {
    addFileUploadTask,
    GlobalAddFileUploadTask,
    GlobalRemoveFileUploadTask,
    GlobalUpdateProgressFileUploadTask,
    removeFileUploadTask,
    updateProgressFileUploadTask,
} from '../../../persisted/global/globalActions';
import { RootState } from '../../../store/rootReducer';
import { ProjectResource } from '../../resources/artifacts/project/projectResource';
import { ExporterService } from './exporterService';
import { ImporterService } from './importerService';
import {
    SystemActionTypes,
    SystemLoadAction,
    SystemLoadFailure,
    SystemLoadRequest,
    SystemLoadSuccess,
    SystemPersistAction,
    SystemPersistFailure,
    SystemPersistRequest,
    SystemPersistSuccess,
} from './types';

export const systemLoadRequest: ActionCreator<SystemLoadRequest> = (filepath: string) => {
    return {
        type: SystemActionTypes.SYSTEM_FILE_LOAD_REQUEST,
        payload: {
            filepath,
        },
    };
};

export const systemLoadSuccess: ActionCreator<SystemLoadSuccess> = (filepath: string) => {
    return {
        type: SystemActionTypes.SYSTEM_FILE_LOAD_SUCCESS,
        payload: {
            filepath,
        },
    };
};

export const systemLoadFailure: ActionCreator<SystemLoadFailure> = (errorMessage: string) => {
    return {
        type: SystemActionTypes.SYSTEM_FILE_LOAD_FAILURE,
        payload: {
            errorMessage,
        },
    };
};

export const loadFilesOnline =
    (
        filePaths: FileList,
        name: string,
        errorLevel: ValidationEntrySeverity
    ): ThunkAction<
        Promise<boolean | null>,
        RootState,
        undefined,
        SystemLoadAction | GlobalAddFileUploadTask | GlobalRemoveFileUploadTask | GlobalUpdateProgressFileUploadTask
    > =>
    (dispatch) => {
        const importId = (Math.random() * (9999 - 1000)).toString(); // TODO vulnerability: betterMathDotRandom doesn't work in here
        // const abortController = new AbortController(); TODO use or remove
        dispatch(addFileUploadTask(importId, `Uploading file(s) for ${name}`, null));
        dispatch(systemLoadRequest(filePaths[0]));

        const systemService = new ImporterService();
        return systemService
            .actions()
            .uploadOnlineFiles(filePaths, name, errorLevel, (progressEvent) => {
                const total = progressEvent.total;
                if (total) {
                    const progress = Math.round((progressEvent.loaded / total) * 100);
                    dispatch(updateProgressFileUploadTask(importId, progress));
                }
            })
            .then(
                (processStarted) => {
                    if (processStarted) {
                        dispatch(systemLoadSuccess(filePaths[0]));
                    } else {
                        dispatch(systemLoadFailure(processStarted));
                    }
                    return processStarted;
                },
                (loadRejectedError) => {
                    dispatch(systemLoadFailure(loadRejectedError));
                    return null;
                }
            )
            .finally(() => {
                dispatch(removeFileUploadTask(importId));
            });
    };

export const systemPersistRequest: ActionCreator<SystemPersistRequest> = (filepath: string) => {
    return {
        type: SystemActionTypes.SYSTEM_PERSIST_REQUEST,
        payload: {
            filepath,
        },
    };
};

export const systemPersistFailed: ActionCreator<SystemPersistFailure> = (errorMessage: string) => {
    return {
        type: SystemActionTypes.SYSTEM_PERSIST_FAILURE,
        payload: {
            errorMessage,
        },
    };
};

export const systemPersistSucceed: ActionCreator<SystemPersistSuccess> = (
    filepath: string,
    system: ProjectResource
) => {
    return {
        type: SystemActionTypes.SYSTEM_PERSIST_SUCCESS,
        payload: {
            filepath,
            system,
        },
    };
};

export const startDownloadPVT =
    (
        name: string,
        projectVersionId: string
    ): ThunkAction<Promise<boolean>, RootState, undefined, SystemPersistAction> =>
    async (dispatch) => {
        let isSaved = false;
        dispatch(systemPersistRequest(''));

        const exporterService = new ExporterService();
        return exporterService
            .actions()
            .startDownloadPVT(name, projectVersionId)
            .then(
                (started) => {
                    isSaved = started;
                    dispatch(systemPersistSucceed('', projectVersionId));
                },
                (refused) => dispatch(systemPersistFailed(refused))
            )
            .then((_) => isSaved);
    };

export const startDownloadRichText =
    (
        name: string,
        processAreaId: string,
        projectVersionId: string
    ): ThunkAction<Promise<boolean>, RootState, undefined, SystemPersistAction> =>
    async (dispatch) => {
        let isSaved = false;
        dispatch(systemPersistRequest(''));

        const exporterService = new ExporterService();
        return exporterService
            .actions()
            .startDownloadRichText(name, processAreaId, projectVersionId)
            .then(
                (started) => {
                    isSaved = started;
                    dispatch(systemPersistSucceed('', processAreaId, projectVersionId));
                },
                (refused) => dispatch(systemPersistFailed(refused))
            )
            .then((_) => isSaved);
    };

export const startDownloadPVTOfLatestVersion =
    (name: string, projectId: string): ThunkAction<Promise<boolean>, RootState, undefined, SystemPersistAction> =>
    async (dispatch) => {
        let isSaved = false;
        dispatch(systemPersistRequest(''));

        const exporterService = new ExporterService();
        return exporterService
            .actions()
            .startDownloadPVTOfLatestVersion(name, projectId)
            .then(
                (started) => {
                    isSaved = started;
                    dispatch(systemPersistSucceed('', projectId));
                },
                (refused) => dispatch(systemPersistFailed(refused))
            )
            .then((_) => isSaved);
    };

export const downloadFromBackgroundTask =
    (
        name: string,
        backgroundTaskId: string,
        fileType: 'pvt' | 'zip' | 'html',
        onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
    ): ThunkAction<Promise<boolean>, RootState, undefined, SystemPersistAction> =>
    async () => {
        const exporterService = new ExporterService();
        return exporterService
            .actions()
            .downloadFromBackgroundTask(name, backgroundTaskId, fileType, onDownloadProgress)
            .then((_) => true);
    };
